
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../apiClient';

/** Match backend coarse groups for <optgroup> labels. */
function authCategory(user) {
  const s = (user.auth_summary || '').toLowerCase();
  if (s.includes('social')) return 'Social sign-in';
  if (s.includes('passwordless')) return 'Passwordless';
  if (s.includes('passkey')) return 'Passkeys';
  if (s.includes('email')) return 'Email / password';
  return 'Other';
}

const CATEGORY_ORDER = ['Email / password', 'Social sign-in', 'Passwordless', 'Passkeys', 'Other'];

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const usersByCategory = useMemo(() => {
    const map = {};
    CATEGORY_ORDER.forEach((c) => {
      map[c] = [];
    });
    users.forEach((u) => {
      const cat = authCategory(u);
      if (!map[cat]) map[cat] = [];
      map[cat].push(u);
    });
    return map;
  }, [users]);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/roles');
      setRoles(response.data.map(r => r.role));
    } catch (err) {
      setError('Failed to fetch roles.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/roles', { role: newRole });
      setNewRole('');
      fetchRoles();
      setMessage(`Role '${newRole}' created successfully.`);
    } catch (err) {
      setError('Failed to create role.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/users/roles/assign', { user_id: selectedUser, role: selectedRole });
      setMessage(`Role '${selectedRole}' assigned to user successfully.`);
    } catch (err) {
      setError('Failed to assign role.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Role Management</h1>

      {error && <p className="text-red-500 text-center mb-4">{error}</p>}
      {message && <p className="text-green-500 text-center mb-4">{message}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Create Role Section */}
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold mb-4">Create New Role</h2>
          <form onSubmit={handleCreateRole}>
            <div className="mb-4">
              <label htmlFor="newRole" className="block text-gray-700 font-bold mb-2">Role Name</label>
              <input
                type="text"
                id="newRole"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Creating...' : 'Create Role'}
            </button>
          </form>
        </div>

        {/* Assign Role Section */}
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-semibold mb-4">Assign Role to User</h2>
          <form onSubmit={handleAssignRole}>
            <div className="mb-4">
              <label htmlFor="user" className="block text-gray-700 font-bold mb-2">Select User</label>
              <select
                id="user"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                required
              >
                <option value="">Select a user</option>
                {CATEGORY_ORDER.map((cat) => {
                  const list = usersByCategory[cat] || [];
                  if (!list.length) return null;
                  return (
                    <optgroup key={cat} label={cat}>
                      {list.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.full_name ? `${user.full_name} (${user.email})` : user.email}
                          {user.auth_summary ? ` — ${user.auth_summary}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="role" className="block text-gray-700 font-bold mb-2">Role</label>
              <select
                id="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                required
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Assigning...' : 'Assign Role'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
